import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { ticketService } from '../../../services/ticketService';

const TicketDetailModal = ({ 
  ticket, 
  onClose, 
  onStatusUpdate, 
  onPriorityUpdate,
  getStatusColor,
  getPriorityColor,
  getStatusText,
  getPriorityText,
  getCategoryText
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [ticket?.id]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const data = await ticketService?.getMessages(ticket?.id);
      setMessages(data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage?.trim()) return;

    try {
      setSending(true);
      await ticketService?.addMessage(ticket?.id, newMessage);
      setNewMessage('');
      await fetchMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Mesaj gönderilirken bir hata oluştu: ' + err?.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e?.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">Talep Detayı</h2>
            <p className="text-sm text-muted-foreground">Talep No: <span className="font-mono text-primary">{ticket?.ticketNumber}</span></p>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Konu</label>
              <p className="text-foreground font-semibold">{ticket?.subject}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Kategori</label>
              <p className="text-foreground">{getCategoryText(ticket?.category)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Durum</label>
              <select
                value={ticket?.status}
                onChange={(e) => onStatusUpdate(ticket?.id, e?.target?.value)}
                className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(ticket?.status)} border-0 cursor-pointer`}
              >
                <option value="open">Açık</option>
                <option value="in_progress">İşlemde</option>
                <option value="resolved">Çözüldü</option>
                <option value="closed">Kapalı</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Öncelik</label>
              <select
                value={ticket?.priority}
                onChange={(e) => onPriorityUpdate(ticket?.id, e?.target?.value)}
                className={`px-3 py-1 rounded-full text-sm font-semibold ${getPriorityColor(ticket?.priority)} border-0 cursor-pointer`}
              >
                <option value="low">Düşük</option>
                <option value="medium">Orta</option>
                <option value="high">Yüksek</option>
                <option value="urgent">Acil</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Oluşturulma Tarihi</label>
              <p className="text-foreground">{new Date(ticket?.createdAt)?.toLocaleString('tr-TR')}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Son Güncelleme</label>
              <p className="text-foreground">{new Date(ticket?.updatedAt)?.toLocaleString('tr-TR')}</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="text-sm font-medium text-muted-foreground mb-2 block">Açıklama</label>
            <div className="bg-muted rounded-lg p-4">
              <p className="text-foreground whitespace-pre-wrap">{ticket?.description}</p>
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="MessageSquare" size={20} />
              Mesajlar
            </h3>

            {loading ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm text-muted-foreground">Mesajlar yükleniyor...</p>
              </div>
            ) : messages?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="MessageCircle" size={48} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">Henüz mesaj yok</p>
              </div>
            ) : (
              <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
                {messages?.map((message) => (
                  <div key={message?.id} className="bg-muted rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                        {message?.senderName?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-foreground">{message?.senderName}</span>
                          {message?.senderRole === 'admin' && (
                            <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">Admin</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{new Date(message?.createdAt)?.toLocaleString('tr-TR')}</p>
                      </div>
                    </div>
                    <p className="text-foreground whitespace-pre-wrap">{message?.message}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e?.target?.value)}
                placeholder="Mesajınızı yazın..."
                rows={3}
                className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
              <Button
                variant="default"
                iconName="Send"
                onClick={handleSendMessage}
                disabled={sending || !newMessage?.trim()}
              >
                {sending ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailModal;